// Provider routing — LITE build (D-159). Unlike the full app's multi-provider
// routing, the lite app reads market data from ONE place: our own backend cache
// (/v1/market/chart), which the Worker refreshes from Yahoo once an hour. Clients
// never call Yahoo directly, so there's no per-user throttling. The rest of the
// app still calls market-data.ts exactly as before and never sees any of this.

import { storageGet, storageSet } from "../storage.js";
import { stockDirectory, type AssetClass } from "../stock-directory.js";
import type { DataProvider, CandleResult, ClosesResult } from "./types.js";
import type { OHLCPoint } from "../market-data.js";
import { freeProvider } from "./free-provider.js";
import { topstepxProvider } from "./topstepx-provider.js";
import { alpacaProvider } from "./alpaca-provider.js";
import { polygonProvider } from "./polygon-provider.js";
import { backtestProvider, isBacktestSymbol } from "./backtest-provider.js";
import { apiBase } from "../license.js";

const SELECTED_KEY = "dataProviderSelected"; // retained for storage-shape compatibility

// Kept so the (now-hidden) connections/data-mode code still type-checks; the lite
// app is Free/backend only, so these providers are never actually routed to.
export const allProviders: DataProvider[] = [freeProvider, topstepxProvider, alpacaProvider, polygonProvider];

export function providerById(id: string): DataProvider | null {
  return allProviders.find((p) => p.id === id) ?? null;
}
export async function selectedProviderId(): Promise<string> {
  return await storageGet<string>(SELECTED_KEY, "free");
}
export async function setSelectedProviderId(id: string): Promise<void> {
  await storageSet(SELECTED_KEY, id);
}

/** Asset class for routing; directory first, symbol shape as fallback. */
export function classifySymbol(symbol: string, directoryCategory: AssetClass | null): AssetClass {
  if (directoryCategory) return directoryCategory;
  const s = symbol.trim().toUpperCase();
  if (s.endsWith("=F")) return "Futures";
  if (s.endsWith("=X")) return "Forex";
  if (s.startsWith("^")) return "Indices";
  if (/^[A-Z0-9]{2,10}-USD$/.test(s)) return "Crypto";
  return "Stocks";
}

// The only bar resolutions the backend caches (mirrors CACHE_INTERVALS on the
// server). Any other interval (intraday) isn't cached in the lite build.
const CACHED_INTERVALS = new Set(["1d", "1wk", "1mo", "3mo"]);

/** One read from our backend cache for a (symbol, interval). Returns empty on
 * anything unexpected so the caller degrades gracefully rather than throwing. */
async function backendChart(symbol: string, interval: string): Promise<{ closes: number[]; dates: number[]; price: number | null }> {
  if (!CACHED_INTERVALS.has(interval)) return { closes: [], dates: [], price: null };
  try {
    const base = await apiBase();
    const res = await fetch(`${base}/v1/market/chart?symbol=${encodeURIComponent(symbol.toUpperCase())}&interval=${interval}`);
    if (!res.ok) return { closes: [], dates: [], price: null };
    const j = (await res.json()) as { closes?: number[]; dates?: number[]; regularMarketPrice?: number | null };
    return { closes: Array.isArray(j.closes) ? j.closes : [], dates: Array.isArray(j.dates) ? j.dates : [], price: j.regularMarketPrice ?? null };
  } catch {
    return { closes: [], dates: [], price: null };
  }
}

export async function routedCandles(symbol: string, _range: string, interval: string): Promise<CandleResult> {
  // Internal review tool: our own historical XAU/USD bars for marking up trades.
  if (isBacktestSymbol(symbol)) return await backtestProvider.candles(symbol, _range, interval);
  const { closes, dates, price } = await backendChart(symbol, interval);
  // The cache stores closes only; synthesise flat OHLC (BB and every lite reading
  // use closes). date aligns with the cached timestamp.
  const candles: OHLCPoint[] = closes.map((c, i) => ({ date: new Date(dates[i] ?? Date.now()), open: c, high: c, low: c, close: c, volume: 0 }));
  return { candles, currentPrice: price ?? (closes.length ? closes[closes.length - 1] : undefined) };
}

export async function routedCloses(symbol: string, _range: string, interval: string): Promise<ClosesResult> {
  if (isBacktestSymbol(symbol)) return await backtestProvider.closes(symbol, _range, interval);
  const { closes, price } = await backendChart(symbol, interval);
  return { closes, regularMarketPrice: price ?? undefined };
}
